/**
 * SpaceActionMenu — shared action dropdown for the space listing table.
 *
 * Base items (always present, in order):
 *   1. Copy S3 URI
 *   2. Copy Space URI
 *   3. View / Edit Metadata
 *   4. [extraMenuItems] — caller injects page-specific items here
 *   5. Delete Space
 *
 * Adding a new base item here automatically surfaces it in every page that
 * uses this component (SpaceFolderGrid, SpaceObjects, etc.).
 */

import React from "react";
import { Dropdown, Menu, Tooltip, message } from "antd";
import { CopyOutlined, DeleteOutlined, LinkOutlined, MoreOutlined, EditOutlined } from "@ant-design/icons";
import AddOrEditSpace from "components/AddOrEditSpace";

// ─── Shared record shape ──────────────────────────────────────────────────────

export interface SpaceActionRecord {
  spaceName: string;
  spaceNameMasked?: string;
  bucketName: string;
  regionName: string;
  gxpFlag: string;
  data_account_id: string;
  /** Optional – used for View/Edit Metadata form */
  sus_git_repo?: string;
  sus_project_metadata?: string;
  metaDataUsers?: string;
  file_system_type?: string;
}

// ─── Props ────────────────────────────────────────────────────────────────────

export interface SpaceActionMenuProps {
  record: SpaceActionRecord;
  domainName: string;

  /**
   * Called when "Delete Space" is clicked.
   * The caller is responsible for showing the confirmation modal.
   */
  onDeleteClick: (record: SpaceActionRecord) => void;

  /** Passed to AddOrEditSpace so it can refresh the list after a save. */
  onRefreshSpaces?: () => void;

  /**
   * When provided, clicking "View/Edit Metadata" fires this callback instead
   * of rendering AddOrEditSpace inline.
   * Use this for the grid, where the drawer is kept alive *outside* the
   * dropdown (avoids unmount-on-close).
   */
  onEditClick?: (record: SpaceActionRecord) => void;

  /**
   * Extra <Menu.Item> / <Menu.Divider> nodes injected between the
   * "View / Edit Metadata" item and "Delete Space".
   * Use this for page-specific actions (Parameters & Secrets,
   * Subscribe/Unsubscribe, View Business Metadata, Retry, etc.).
   */
  extraMenuItems?: React.ReactNode;

  /** Ant Design Dropdown trigger. Defaults to ["hover"]. */
  trigger?: ("click" | "hover" | "contextMenu")[];

  /** Controlled open state – wire up for click-triggered dropdowns. */
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

// ─── Shared styles ────────────────────────────────────────────────────────────

const baseItemStyle: React.CSSProperties = {
  color: "#1677ff",
  fontWeight: 500,
  fontSize: 15,
  paddingLeft: 16,
};

const deleteItemStyle: React.CSSProperties = {
  color: "#ff4d4f",
  fontWeight: 500,
  fontSize: 15,
  paddingLeft: 16,
};

// ─── Component ───────────────────────────────────────────────────────────────

const SpaceActionMenu: React.FC<SpaceActionMenuProps> = ({
  record,
  domainName,
  onDeleteClick,
  onRefreshSpaces,
  onEditClick,
  extraMenuItems,
  trigger = ["hover"],
  isOpen,
  onOpenChange,
}) => {
  const preferredSpaceName = record.spaceNameMasked || record.spaceName;

  const overlay = (
    <Menu className="action-menu-custom">
      {/* ── 1. Copy S3 URI ─────────────────────────────────────────────── */}
      <Menu.Item
        key="copyS3Uri"
        style={baseItemStyle}
        onClick={async (e) => {
          e.domEvent.stopPropagation();
          try {
            await navigator.clipboard.writeText(`s3://${record.bucketName}`);
            message.success("Copied S3 URI to clipboard!");
          } catch {
            message.error("Failed to copy URI");
          }
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <LinkOutlined style={{ fontSize: 16 }} />
          Copy S3 URI
        </span>
      </Menu.Item>

      {/* ── 2. Copy Space URI ───────────────────────────────────────────── */}
      <Menu.Item
        key="copySpaceUri"
        style={baseItemStyle}
        onClick={async (e) => {
          e.domEvent.stopPropagation();
          try {
            const spaceURI = `${window.location.origin}/explorer/${encodeURIComponent(domainName)}/${encodeURIComponent(preferredSpaceName)}`;
            await navigator.clipboard.writeText(spaceURI);
            message.success("Copied Space URI to clipboard!");
          } catch {
            message.error("Failed to copy URI");
          }
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <CopyOutlined style={{ fontSize: 16 }} />
          Copy Space URI
        </span>
      </Menu.Item>

      {/* ── 3. View / Edit Metadata ─────────────────────────────────────── */}
      <Menu.Item key="editSpace" style={baseItemStyle}>
        <span style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {onEditClick ? (
            /* Grid pattern: trigger drawer that lives outside the dropdown */
            <span
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                color: "#1677ff",
                fontWeight: 500,
                fontSize: 15,
                cursor: "pointer",
              }}
              onClick={(e) => {
                e.stopPropagation();
                onEditClick(record);
              }}
            >
              <EditOutlined style={{ fontSize: 16 }} />
              View/Edit Metadata
            </span>
          ) : (
            /* Default: inline AddOrEditSpace (SpaceObjects pattern) */
            <AddOrEditSpace
              mode="edit"
              editPayload={{
                bucketName: record.spaceName,
                spaceName: record.spaceName,
                region: record.regionName,
                domainName,
                actualBucketName: record.bucketName,
              }}
              onSuccess={onRefreshSpaces}
              buttonText="View/Edit Metadata"
              buttonProps={{
                type: "link",
                style: {
                  padding: 0,
                  color: "#1677ff",
                  fontWeight: 500,
                  background: "none",
                  border: "none",
                  boxShadow: "none",
                  height: "auto",
                  textAlign: "left",
                  fontSize: 15,
                },
              }}
              domainName={domainName}
            />
          )}
        </span>
      </Menu.Item>

      {/* ── 4. Page-specific items (grid: Parameters, Subscribe, etc.) ──── */}
      {extraMenuItems}

      {/* ── 5. Delete Space ─────────────────────────────────────────────── */}
      <Menu.Item
        key="deleteSpace"
        style={deleteItemStyle}
        onClick={(e) => {
          e.domEvent.stopPropagation();
          onDeleteClick(record);
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <DeleteOutlined style={{ fontSize: 16 }} />
          Delete Space
        </span>
      </Menu.Item>
    </Menu>
  );

  return (
    <Dropdown
      overlay={overlay}
      trigger={trigger}
      placement="bottomLeft"
      overlayClassName="action-menu-dropdown"
      open={isOpen}
      onOpenChange={onOpenChange}
    >
      <span
        style={{
          cursor: "pointer",
          fontSize: 21,
          color: "#1677ff",
          padding: "6px 12px",
          borderRadius: 6,
          display: "inline-block",
          transition: "background 0.2s",
        }}
      >
        <MoreOutlined />
      </span>
    </Dropdown>
  );
};

export default SpaceActionMenu;
